import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Heart, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../lib/i18n";
import { track } from "../lib/ga";

export default function Wins() {
  const { t, lang } = useLang();
  const [content, setContent] = useState("");
  const [wins, setWins] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/wins").then((r) => setWins(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/wins", { content: content.trim(), lang });
      track.winShared();
      setContent("");
      setWins((w) => [data, ...w]);
      toast.success(t("wins.saved"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="wins-page">
      <header>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#81B29A]/15 text-[#73A38B] text-xs font-semibold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5" /> {t("wins.badge")}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B] mt-3">{t("wins.title")}</h1>
        <p className="text-[#8A8D91] mt-2 max-w-2xl">{t("wins.subtitle")}</p>
      </header>

      <form onSubmit={submit} className="bg-white border border-[#E8E6E1] rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex gap-2 flex-col sm:flex-row" data-testid="wins-form">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value.slice(0, 280))}
          rows={2}
          maxLength={280}
          placeholder={t("wins.placeholder")}
          data-testid="wins-input"
          className="flex-1 bg-[#FDFBF7] border border-[#E8E6E1] rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
        />
        <button
          type="submit"
          disabled={busy || !content.trim()}
          data-testid="wins-submit"
          className="bg-[#81B29A] text-white rounded-full px-5 py-3 font-medium hover:bg-[#73A38B] transition-colors disabled:opacity-50 inline-flex items-center gap-2 self-start"
        >
          <Send className="w-4 h-4" /> {t("wins.submit")}
        </button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wins-list">
        {wins.length === 0 && (
          <div className="col-span-full text-center text-[#8A8D91] italic py-8">{t("wins.empty")}</div>
        )}
        {wins.map((w) => (
          <article
            key={w.id}
            data-testid={`win-${w.id}`}
            className="bg-white border border-[#E8E6E1] rounded-3xl p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex gap-3"
          >
            <div className="w-10 h-10 rounded-full bg-[#81B29A]/15 grid place-items-center text-[#73A38B] shrink-0">
              <Heart className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-[#3D405B] leading-relaxed">{w.content}</p>
              <div className="text-xs text-[#8A8D91] mt-2">
                {new Date(w.created_at).toLocaleDateString(t("locale"), { day: "numeric", month: "long" })}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
